import type { Receipt } from '../types.js';
import type { SplitContext } from '../split/dsl.js';
import type { SplitReply } from '../split/reply.js';
import type { SplitHistory } from './prompts.js';
import { InferenceError, type InferenceProvider, type ReceiptInput } from './provider.js';
import { parseReceiptDraft, parseSplitReply } from './schema.js';

/**
 * Talks to our own API, not to a model vendor.
 *
 * The API key cannot live in a browser, so a thin server endpoint stands
 * between this and whichever model is in use. That indirection is also what
 * keeps `packages/core` vendor-neutral: swapping Gemini for Cerebras or Groq is
 * a change to the server function alone, and nothing here moves.
 *
 * Server responses are re-validated through the same parsers used everywhere
 * else. The server is not trusted to have got it right — it is running the same
 * fallible model, and a malformed receipt reaching the settlement code would be
 * far worse than a clean error here.
 */

export interface HttpProviderOptions {
  /** Defaults to same-origin `/api`. */
  baseUrl?: string;
  /** Sent as `x-app-token`. A shared secret to keep the proxy from being scraped. */
  token?: string;
  fetchImpl?: typeof fetch;
}

interface Endpoints {
  receipt: string;
  split: string;
}

export class HttpProvider implements InferenceProvider {
  readonly kind = 'cloud' as const;
  private readonly endpoints: Endpoints;
  private readonly token: string | undefined;
  private readonly fetchImpl: typeof fetch;

  constructor(options: HttpProviderOptions = {}) {
    const base = (options.baseUrl ?? '/api').replace(/\/$/, '');
    this.endpoints = {
      receipt: `${base}/parse-receipt`,
      split: `${base}/parse-split`,
    };
    this.token = options.token;
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  private async post(url: string, body: unknown, signal?: AbortSignal): Promise<unknown> {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (this.token) headers['x-app-token'] = this.token;

    let response: Response;
    try {
      response = await this.fetchImpl(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        ...(signal ? { signal } : {}),
      });
    } catch (error) {
      // Offline, DNS failure, CORS. Distinct from "the model misbehaved" and
      // worth a different message in the UI.
      throw new InferenceError('Could not reach the server', [
        error instanceof Error ? error.message : String(error),
      ]);
    }

    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      throw new InferenceError(
        response.status === 429
          ? 'Too many requests — the daily quota may be used up'
          : `Server returned ${response.status}`,
        detail ? [detail.slice(0, 500)] : [],
      );
    }

    try {
      return await response.json();
    } catch {
      throw new InferenceError('Server returned a malformed response');
    }
  }

  async structureReceipt(input: ReceiptInput, signal?: AbortSignal): Promise<Receipt> {
    const payload =
      input.kind === 'image'
        ? { imageBase64: input.base64, mimeType: input.mimeType }
        : { rows: input.rows };

    const raw = await this.post(this.endpoints.receipt, payload, signal);
    const parsed = parseReceiptDraft(raw);
    if (!parsed.ok) {
      throw new InferenceError('Could not read the receipt', parsed.errors);
    }
    return parsed.value;
  }

  async parseSplit(
    context: SplitContext,
    utterance: string,
    signal?: AbortSignal,
    history?: SplitHistory,
  ): Promise<SplitReply> {
    const raw = await this.post(
      this.endpoints.split,
      {
        // Only what the prompt needs — no prices are required to decide who
        // pays for what, so they stay on the device.
        items: context.items.map((item) => ({
          id: item.id,
          name: item.name,
          category: item.category,
          lineTotalCents: item.lineTotal,
        })),
        people: context.people,
        speakerId: context.speakerId,
        utterance,
        ...(history && history.messages.length > 0 ? { history } : {}),
      },
      signal,
    );

    const parsed = parseSplitReply(raw);
    if (!parsed.ok) {
      throw new InferenceError('Could not understand the split', parsed.errors);
    }
    return parsed.value;
  }
}
